// Auth state machine of spec §3, as a pure reducer.
//   anonymous → password_ok_needs_totp → authenticated
//   anonymous → setup_required → authenticated
import type { LoginResponse, Me } from "@/api";

export type SessionState =
  | { status: "restoring"; token: string }
  | { status: "anonymous"; notice?: string }
  | { status: "password_ok_needs_totp"; pendingToken: string }
  | { status: "setup_required"; token: string; me: Me }
  | { status: "authenticated"; token: string; me: Me };

export type SessionAction =
  | { type: "login"; res: LoginResponse }
  | { type: "totp_ok"; res: LoginResponse }
  | { type: "pending_expired" }
  | { type: "me"; me: Me }
  | { type: "signed_out"; notice?: string };

export const needsSetup = (me: Me): boolean => Boolean(me.totp_required && !me.totp_enabled);

function withToken(token: string | undefined, me: Me | undefined, state: SessionState): SessionState {
  if (!token || !me) return state;
  return { status: needsSetup(me) ? "setup_required" : "authenticated", token, me };
}

export function initialSession(storedToken: string | null): SessionState {
  return storedToken ? { status: "restoring", token: storedToken } : { status: "anonymous" };
}

export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case "login": {
      const { res } = action;
      if (res.requires_totp && res.pending_token)
        return { status: "password_ok_needs_totp", pendingToken: res.pending_token };
      return withToken(res.token, res.user, state);
    }
    case "totp_ok":
      return state.status === "password_ok_needs_totp" ? withToken(action.res.token, action.res.user, state) : state;
    case "pending_expired":
      return state.status === "password_ok_needs_totp" ?
          { status: "anonymous", notice: "Too many wrong codes or the sign-in expired. Enter your password again." }
        : state;
    case "me":
      return "token" in state ? withToken(state.token, action.me, state) : state;
    case "signed_out":
      return { status: "anonymous", notice: action.notice };
  }
}

export const tokenOf = (s: SessionState): string | null => ("token" in s ? s.token : null);
export const meOf = (s: SessionState): Me | null => ("me" in s ? s.me : null);
